import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Zap, AlertTriangle, Clock } from "lucide-react";

/**
 * RapidAPI JSearch quota widget for the Admin overview.
 * Reads from `GET /api/admin/jsearch-quota` (populated by every real search
 * upstream call). Highlights at ≥80% used so the admin can upgrade before
 * hitting the hard limit.
 */

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

export default function AdminJSearchQuotaWidget() {
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/jsearch-quota");
        if (!cancelled) setQ(r.data || null);
      } catch {
        if (!cancelled) setQ(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!q || !q.enabled) {
    return (
      <section className="mt-4" data-testid="admin-jsearch-quota-empty">
        <div className="ed-card p-6">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
              JSearch — RapidAPI quota
            </p>
          </div>
          <p className="font-serif text-lg text-neutral-800">Nog geen data</p>
          <p className="text-xs text-neutral-500 mt-1 leading-relaxed max-w-md">
            De widget vult zichzelf zodra de eerste vacaturezoekopdracht via JSearch loopt.
            Wanneer de subscription niet actief is blijft deze leeg.
          </p>
        </div>
      </section>
    );
  }

  const usedPct = q.used_pct ?? 0;
  const remainingPct = q.limit ? Math.max(0, 100 - usedPct) : 0;
  const isWarn = usedPct >= 80;
  const isDanger = usedPct >= 95;
  const barTone = isDanger ? "bg-rose-700" : isWarn ? "bg-amber-500" : "bg-emerald-700";
  const textTone = isDanger ? "text-rose-700" : isWarn ? "text-amber-700" : "text-emerald-700";

  return (
    <section className="mt-4" data-testid="admin-jsearch-quota">
      <div className="ed-card p-6">
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <div>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 inline-flex items-center gap-1.5">
              <Zap size={11} strokeWidth={1.5} /> JSearch — RapidAPI quota
            </p>
            <p className="font-serif text-2xl mt-1" data-testid="jsearch-quota-remaining">
              {q.remaining?.toLocaleString() ?? "—"} van {q.limit?.toLocaleString() ?? "—"} verzoeken over
            </p>
          </div>
          {isWarn && (
            <span
              data-testid="jsearch-quota-warn-badge"
              className={`inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${isDanger ? "text-rose-800 border-rose-300 bg-rose-50" : "text-amber-800 border-amber-300 bg-amber-50"}`}
            >
              <AlertTriangle size={11} strokeWidth={2} /> {isDanger ? "kritiek" : "waarschuwing"}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="relative h-3 bg-neutral-100 border border-neutral-200 overflow-hidden">
          <div
            className={`h-full ${barTone} transition-all duration-500 ease-out`}
            style={{ width: `${Math.min(100, usedPct)}%` }}
            data-testid="jsearch-quota-bar"
          />
        </div>

        <div className="mt-3 flex items-baseline justify-between gap-3">
          <p className={`font-mono text-xs ${textTone}`} data-testid="jsearch-quota-pct">
            {usedPct.toFixed(1)}% gebruikt · {remainingPct.toFixed(1)}% over
          </p>
          <p className="font-mono text-[0.65rem] text-neutral-500 inline-flex items-center gap-1">
            <Clock size={10} strokeWidth={1.5} /> reset {fmtDate(q.reset_at)}
          </p>
        </div>

        {isWarn && (
          <p className="mt-4 text-xs text-neutral-700 leading-relaxed border-l-2 border-amber-400 pl-3" data-testid="jsearch-quota-upgrade-hint">
            {isDanger
              ? "Bijna op — nieuwe zoekopdrachten kunnen falen. Upgrade op RapidAPI naar het PRO plan ($9,99/mnd, 500/dag) om onderbrekingen te voorkomen."
              : "80% van de maandelijkse quota gebruikt. Overweeg te upgraden op RapidAPI voordat je het limiet raakt."}
            {" "}
            <a
              href="https://rapidapi.com/letscrape-6bRBa3QguO5/api/jsearch/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[0.65rem] uppercase tracking-[0.12em] underline underline-offset-2 hover:text-neutral-900"
              data-testid="jsearch-quota-upgrade-link"
            >
              Bekijk plannen →
            </a>
          </p>
        )}
      </div>
    </section>
  );
}
